import { ChangeEvent, KeyboardEvent, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FormNode } from '@/utils/api/aboboApiTypes';
import BaseQuestionType from './BaseQuestionType';

// Import shadcn components
import { Input } from '@/components/ui/input';
import QuestionHeading from '@/components/QuestionHeading';
import QuestionHelperText from '@/components/QuestionHelperText';

interface NumericProps {
  node: FormNode;
}

export default forwardRef<any, NumericProps>(function Numeric(
  { node },
  ref
) {
  const { t } = useTranslation();

  const validateNumeric = (values: any[]) => {
    const value = values?.[0]?.data || '';
    
    if (node.metadata?.required && !value) {
      return t('questionTypes.numeric.validation.required');
    }
    
    if (value) {
      const numValue = parseFloat(value);
      if (isNaN(numValue)) {
        return t('questionTypes.numeric.validation.invalidNumber');
      }
      
      // Add any additional numeric validation here
      if (numValue < 0) {
        return t('questionTypes.numeric.validation.negative');
      }
    }
    
    return true;
  };

  const transformInput = (values: any[]) => {
    return values?.[0]?.data || '';
  };

  const transformOutput = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event?.target?.value || '';
    return [{ data: value }];
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;

    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'Enter', 'Home', 'End',
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'
    ];
    
    if (allowedKeys.includes(e.key)) return;
    
    if (/\d/.test(e.key)) return;

    if (e.key === '.' && !value.includes('.')) return;

    if (e.key === '-') return;
    
    e.preventDefault();
  };

  return (
    <BaseQuestionType
      node={node}
      validate={validateNumeric}
      transform={{
        input: transformInput,
        output: transformOutput,
      }}
    >
      {(field) => (
        <div className="space-y-4" ref={ref}>
          <QuestionHeading required={node.metadata?.required}>
            {node.body}
          </QuestionHeading>
          
          {node.helperText && (
            <QuestionHelperText>
              {node.helperText}
            </QuestionHelperText>
          )}

          <Input
            {...field}
            autoComplete="off"
            onKeyDown={handleKeyDown}
            type="number"
            placeholder={t('questionTypes.numeric.placeholder')}
            min="0"
            step="any"
            className="w-full"
          />

          {field.error && (
            <p className="text-red-500 text-sm">{field.error}</p>
          )}
        </div>
      )}
    </BaseQuestionType>
  );
});
